import React, { useState, useEffect, useRef, useCallback } from 'react'
import { MapPinIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid'
import mapboxgl from 'mapbox-gl'

const LocationSearch = ({
  onLocationSelect,
  eventLocation,
  radius,
  className = '',
  placeholder = "Search for an address...",
  disabled = false
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const containerRef = useRef(null)

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3958.8
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  const toRad = (value) => (value * Math.PI) / 180

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      let geocodingUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        query
      )}.json?access_token=${mapboxgl.accessToken}&types=address&limit=10`

      if (eventLocation && eventLocation.latitude && eventLocation.longitude) {
        geocodingUrl += `&proximity=${eventLocation.longitude},${eventLocation.latitude}`
      }

      const response = await fetch(geocodingUrl)
      const data = await response.json()
      let results = data.features || []

      if (eventLocation && radius && results.length > 0) {
        results = results.map(result => {
          const [lng, lat] = result.center
          const distance = calculateDistance(
            parseFloat(eventLocation.latitude),
            parseFloat(eventLocation.longitude),
            lat,
            lng
          )
          return {
            ...result,
            distanceFromEvent: distance,
            withinRadius: distance <= parseFloat(radius)
          }
        })

        results.sort((a, b) => {
          if (a.withinRadius && !b.withinRadius) return -1
          if (!a.withinRadius && b.withinRadius) return 1
          return a.distanceFromEvent - b.distanceFromEvent
        })

        results = results.slice(0, 5)
      }

      setSearchResults(results)
    } catch (error) {
      console.error('Error searching locations:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  useEffect(() => {
    if (disabled) return
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery)
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [searchQuery, disabled, eventLocation, radius])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSearchResults([])
        setIsFocused(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleResultClick = (result) => {
    if (disabled) return

    const [lng, lat] = result.center

    if (eventLocation && radius) {
      const distance = calculateDistance(
        parseFloat(eventLocation.latitude),
        parseFloat(eventLocation.longitude),
        lat,
        lng
      )

      if (distance > parseFloat(radius)) {
        if (onLocationSelect) {
          onLocationSelect(null, `Please select a location within ${radius} miles of the event.`)
        }
        return
      }
    }

    const location = {
      address: result.place_name.split(',')[0].trim(),
      city: result.context?.find(c => c.id.startsWith('place'))?.text || '',
      state: result.context?.find(c => c.id.startsWith('region'))?.short_code?.replace(/^US-/, '') || result.context?.find(c => c.id.startsWith('region'))?.text || '',
      zip_code: result.context?.find(c => c.id.startsWith('postcode'))?.text || '',
      latitude: lat.toString(),
      longitude: lng.toString()
    }

    if (onLocationSelect) {
      onLocationSelect(location, null)
    }

    setSearchResults([])
    setSearchQuery('')
    setIsFocused(false)
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
          <MagnifyingGlassIcon className={`h-5 w-5 transition-colors duration-150 ${
            isFocused ? 'text-primary-500' : 'text-text-muted'
          }`} />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full rounded-lg border bg-bg text-text text-base
            min-h-[48px] pl-11 pr-10
            placeholder:text-text-muted
            transition-all duration-200
            disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-bg-secondary
            ${isFocused
              ? 'border-primary-500 ring-2 ring-primary-500/20'
              : 'border-border hover:border-border-strong'
            }
          `}
        />
        {isSearching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-surface-200 border-t-primary-500" />
          </div>
        )}
      </div>

      {searchResults.length > 0 && !disabled && (
        <div className="absolute z-dropdown mt-1.5 w-full rounded-xl border border-border bg-bg-elevated shadow-lg overflow-hidden">
          <ul className="max-h-64 overflow-auto py-1.5">
            {searchResults.map((result) => (
              <li
                key={result.id}
                className={`
                  cursor-pointer px-4 py-3
                  hover:bg-surface-100 active:bg-surface-200
                  transition-colors duration-100
                  ${result.withinRadius ? 'border-l-3 border-l-green-500' :
                    result.distanceFromEvent ? 'border-l-3 border-l-amber-400' : ''}
                `}
                onClick={() => handleResultClick(result)}
              >
                <div className="flex items-start gap-3">
                  <MapPinIcon className="h-5 w-5 text-text-muted flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text truncate">
                      {result.place_name.split(',')[0]}
                    </p>
                    <p className="text-xs text-text-muted truncate mt-0.5">
                      {result.place_name.split(',').slice(1).join(',').trim()}
                    </p>
                    {result.distanceFromEvent != null && (
                      <div className="flex items-center gap-1.5 mt-1">
                        {result.withinRadius ? (
                          <>
                            <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" />
                            <span className="text-xs font-medium text-green-600">
                              Within event area ({result.distanceFromEvent.toFixed(1)} mi)
                            </span>
                          </>
                        ) : (
                          <>
                            <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-500" />
                            <span className="text-xs font-medium text-amber-600">
                              {result.distanceFromEvent.toFixed(1)} mi from event
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default LocationSearch